import { useState } from "react";
import {
  FaHome,
  FaServicestack,
  FaPaperclip,
  FaUserAlt,
  FaPowerOff,
} from "react-icons/fa";
import { FiChevronDown as FiChevronDownIcon } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

const menuItems = [
  {
    name: "My Dashboard",
    link: "/dashboard",
    icon: <FaHome />,
  },
  {
    name: "Examinations",
    link: "#",
    icon: <FaServicestack />,
    submenu: [
      { name: "My Exams", link: "/exams" },
      { name: "My Results", link: "/results" },
    ],
  },
  {
    name: "Assignments",
    link: "#",
    icon: <FaPaperclip />,
    submenu: [
      { name: "My Assignments", link: "#" },
      { name: "Completed Assignments", link: "#" },
      { name: "Pending Assignments", link: "#" },
      { name: "Assignment Marks", link: "#" },
    ],
  },
  {
    name: "My Account",
    link: "#",
    icon: <FaUserAlt />,
    submenu: [
      { name: "My Profile", link: "/profile" },
      { name: "Change Password", link: "#" },
    ],
  },
  {
    name: "Logout",
    link: "/login",
    icon: <FaPowerOff />,
    isLogout: true,
  },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("parentToken");
    localStorage.removeItem("selectedStudentId");
    sessionStorage.removeItem("student");
    toast.dismiss(); // Clear all toasts
    setIsOpen(false);
    navigate("/login");
  };

  return (
    <nav className="bg-white shadow-sm fixed w-full top-0 z-50 border-b border-slate-200">
      <div className="max-w-screen-xl flex items-center justify-between mx-auto p-4">
        {/* Logo & Brand */}
        <Link
          to="/dashboard"
          className="flex items-center space-x-3 rtl:space-x-reverse"
        >
          <div className="p-1 bg-slate-50 rounded-full border border-slate-100">
            <img src="/Pragya-Sarathi.png" className="h-10 w-auto" alt="Logo" />
          </div>
          <span className="text-slate-800 text-xl font-bold tracking-tight">
            EEA <sup className="text-pragyaBlue font-semibold text-xs">(ERP 2.0)</sup>
          </span>
        </Link>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden bg-slate-50 p-2.5 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-pragyaBlue transition-colors focus:outline-none border border-slate-200"
        >
          {isOpen ? (
            <span className="text-xl font-bold">&times;</span>
          ) : (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-80 bg-white shadow-2xl transform ${isOpen ? "translate-x-0" : "translate-x-full"
          } transition-transform duration-300 ease-in-out border-l border-slate-100 text-slate-800`}
      >
        {/* Drawer Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="absolute inset-0 bg-pragyaBlue/20 rounded-full blur-sm"></div>
              <img src="/Pragya-Sarathi.png" alt="User" className="w-12 h-12 rounded-full relative border-2 border-white shadow-sm" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Welcome</p>
              <p className="text-slate-900 font-bold text-lg leading-tight">User</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-200 text-slate-600 hover:bg-pragyaOrange/10 hover:text-pragyaOrange transition-colors"
          >
            &times;
          </button>
        </div>

        {/* Drawer Menu */}
        <ul className="p-4 space-y-1">
          {menuItems.map((item, index) => (
            <li key={index}>
              {item.isLogout ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-between w-full p-3 rounded-xl transition-all duration-200 text-slate-600 hover:bg-red-50 hover:text-red-600"
                >
                  <div className="flex items-center space-x-3">
                    <span className="p-2 rounded-lg bg-slate-100/50 text-slate-400">
                      {item.icon}
                    </span>
                    <span>{item.name}</span>
                  </div>
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (item.submenu) {
                      setSubmenuOpen(submenuOpen === index ? null : index);
                    } else {
                      navigate(item.link);
                      setIsOpen(false);
                    }
                  }}
                  className={`flex items-center justify-between w-full p-3 rounded-xl transition-all duration-200 ${submenuOpen === index
                    ? "bg-slate-50 text-pragyaBlue font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-pragyaBlue"
                    }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className={`p-2 rounded-lg ${submenuOpen === index ? "bg-white shadow-sm text-pragyaBlue" : "bg-slate-100/50 text-slate-400"}`}>
                      {item.icon}
                    </span>
                    <span>{item.name}</span>
                  </div>
                  {item.submenu && (
                    <FiChevronDownIcon
                      className={`transition-transform duration-200 ${submenuOpen === index ? "rotate-180 text-pragyaBlue" : "text-slate-400"
                        }`}
                    />
                  )}
                </button>
              )}

              {/* Submenu */}
              <AnimatePresence>
                {item.submenu && submenuOpen === index && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className="ml-12 mt-1 space-y-1 border-l-2 border-slate-100 pl-4 py-1 overflow-hidden"
                  >
                    {item.submenu.map((subItem, subIndex) => (
                      <li key={subIndex}>
                        <Link
                          to={subItem.link}
                          onClick={() => setIsOpen(false)}
                          className="block px-3 py-2 text-sm text-slate-500 hover:text-pragyaOrange hover:translate-x-1 transition-all rounded-lg"
                        >
                          {subItem.name}
                        </Link>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>
          ))}
        </ul>

        {/* Footer info or decorative element could go here */}
        <div className="absolute bottom-0 w-full p-4 text-center text-xs text-slate-300">
          EEA System v2.0
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
